// InvoiceQboBadge.jsx

import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { useBase } from 'context/BaseContext';

import Button from 'components/button/Button';
import {
  enqueueInvoiceQboSync,
  readInvoiceQboSync,
  readPublicQuickBooks,
} from 'services/quickbooks/QuickBooksService';

function formatRelativeSync(value) {
  const raw = value?.toDate?.() || value;
  const ms = raw instanceof Date ? raw.getTime() : Date.parse(String(raw || ''));
  if (!Number.isFinite(ms)) return '';
  const delta = Date.now() - ms;
  if (delta < 60 * 1000) return 'just now';
  if (delta < 60 * 60 * 1000) return `${Math.floor(delta / 60000)}m ago`;
  if (delta < 24 * 60 * 60 * 1000) return `${Math.floor(delta / 3600000)}h ago`;
  return new Date(ms).toLocaleDateString();
}

function skippedCopy(reason) {
  if (reason === 'no_line_items') return 'Not synced: add line items';
  if (reason === 'no_client') return 'Not synced: set Bill To';
  if (reason === 'voided_before_sync') return 'Not synced: voided before QuickBooks';
  if (reason === 'draft') return '';
  return reason ? `Not synced: ${reason}` : 'Not synced';
}

/**
 * QuickBooks sync indicator on the invoice toolbar.
 */
const InvoiceQboBadge = React.memo(({
  node,
  canManage = false,
}) => {
  const { activeBase } = useBase();
  const [busy, setBusy] = useState(false);

  const connection = useMemo(() => readPublicQuickBooks(activeBase), [activeBase]);
  const sync = useMemo(() => readInvoiceQboSync(node), [node]);

  const visible = Boolean(connection.connected && sync.syncStatus && sync.syncStatus !== 'not_applicable');

  const handleRetry = useCallback(async () => {
    if (!canManage || busy || !node?.key) return;
    setBusy(true);
    try {
      const result = await enqueueInvoiceQboSync({
        baseKey: node.baseKey || activeBase?.key,
        invoiceNode: node,
        reason: 'manual',
      });
      if (result?.status === 'synced') {
        toast.success('Synced to QuickBooks.', { autoClose: 3000 });
      } else if (result?.status === 'skipped') {
        toast.info(skippedCopy(result.reason) || 'Invoice was not sent to QuickBooks.', { autoClose: 4000 });
      } else if (result?.status === 'not_connected' || result?.status === 'mapping_incomplete') {
        toast.info('Connect and map QuickBooks in Settings → Financials.', { autoClose: 4000 });
      } else if (result?.reason) {
        toast.error(result.reason, { autoClose: 5000 });
      }
    } finally {
      setBusy(false);
    }
  }, [activeBase?.key, busy, canManage, node]);

  if (!visible) return null;

  let copy = '';
  let stateClass = '';
  if (sync.syncStatus === 'pending' || busy) {
    copy = 'Syncing to QuickBooks…';
    stateClass = 'is-pending';
  } else if (sync.syncStatus === 'synced') {
    const when = formatRelativeSync(sync.lastSyncedAt);
    copy = when ? `Synced ${when}` : 'Synced to QuickBooks';
    stateClass = 'is-synced';
  } else if (sync.syncStatus === 'error') {
    copy = sync.syncError ? `Couldn’t sync: ${sync.syncError}` : 'Couldn’t sync to QuickBooks';
    stateClass = 'is-error';
  } else if (sync.syncStatus === 'skipped') {
    copy = skippedCopy(sync.syncSkippedReason);
    stateClass = 'is-skipped';
  }

  if (!copy) return null;

  const showAction = Boolean(
    canManage
    && !busy
    && (sync.syncStatus === 'synced' || sync.syncStatus === 'error' || sync.syncStatus === 'skipped'),
  );

  return (
    <div className={`invoice-qbo-badge ${stateClass}`}>
      <span className="invoice-qbo-badge-copy">{copy}</span>
      {showAction ? (
        <Button
          label={sync.syncStatus === 'synced' ? 'Sync now' : 'Retry'}
          height="24px"
          fontSize="11px"
          onClick={handleRetry}
        />
      ) : null}
    </div>
  );
});

InvoiceQboBadge.displayName = 'InvoiceQboBadge';

export default InvoiceQboBadge;
